import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import { ThemeProvider } from './contexts/ThemeContext';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import Onboarding from './pages/Onboarding';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Dashboard from './pages/Dashboard';
import Transactions from './pages/Transactions';
import Budgets from './pages/Budgets';
import Goals from './pages/Goals';
import Investments from './pages/Investments';
import Bills from './pages/Bills';
import Loans from './pages/Loans';
import FinancialPlanner from './pages/FinancialPlanner';
import ClientDashboard from './pages/advisor/ClientDashboard';
import CreateProfessionalPlan from './pages/advisor/CreateProfessionalPlan';
import ViewProfessionalPlan from './pages/advisor/ViewProfessionalPlan';
import MyPlans from './pages/advisor/MyPlans';
import AdvisorSettings from './pages/advisor/AdvisorSettings';
import AdvisorSignup from './pages/advisor/AdvisorSignup';
import AdvisorLogin from './pages/advisor/AdvisorLogin';
import AdvisorDashboard from './pages/advisor/AdvisorDashboard';
import AdvisorPricing from './pages/advisor/AdvisorPricing';
import AdvisorLandingPage from './pages/AdvisorLandingPage';
import ManageAdvisors from './pages/admin/ManageAdvisors';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AccountSettings from './pages/AccountSettings';
import Reports from './pages/Reports';
import AIAdvisor from './pages/AIAdvisor';
import PortfolioAnalyzer from './pages/PortfolioAnalyzer';
import SpendingInsights from './pages/SpendingInsights';
import RetirementCalculator from './pages/RetirementCalculator';
import NetWorthTracker from './pages/NetWorthTracker';
import SmartAlerts from './pages/SmartAlerts';
import FinancialCalculators from './pages/FinancialCalculators';
import LoanCalculators from './pages/LoanCalculators';
import DiscoverInvestments from './pages/DiscoverInvestments';
import StockScreener from './pages/StockScreener';
import MarketNews from './pages/MarketNews';
import QualityPicks from './pages/QualityPicks';
import LearnHome from './pages/LearnHome';
import WhyInvest from './pages/lessons/WhyInvest';
import RiskReturn from './pages/lessons/RiskReturn';
import Compounding from './pages/lessons/Compounding';
import FinancialStatements from './pages/lessons/FinancialStatements';
import KeyMetrics from './pages/lessons/KeyMetrics';
import IntrinsicValueLesson from './pages/lessons/IntrinsicValueLesson';
import BuffettPhilosophy from './pages/lessons/BuffettPhilosophy';
import MarginOfSafety from './pages/lessons/MarginOfSafety';
import IntrinsicValueCalculator from './pages/calculators/IntrinsicValueCalculator';
import StockValuationCalculator from './pages/calculators/StockValuationCalculator';
import ForgotPassword from './pages/ForgotPassword';
import AdvisorAIChat from './pages/advisor/AdvisorAIChat';
import ScrollToTop from './components/ScrollToTop';
import { Toaster } from './components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(!!localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setIsAuthLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      // Update cached role in case it changed
      if (response.data?.role) {
        localStorage.setItem('userRole', response.data.role);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      handleLogout();
    } finally {
      setIsAuthLoading(false);
    }
  };

  const handleLogin = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    // Cache user role to prevent navigation flash on page refresh
    if (userData?.role) {
      localStorage.setItem('userRole', userData.role);
    }
    setToken(newToken);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('onboarding_completed');
    localStorage.removeItem('userRole');
    setToken(null);
    setUser(null);
  };

  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
          {/* Public Routes */}
          <Route
            path="/"
            element={!token ? <LandingPage /> : <Navigate to="/dashboard" />}
          />
          <Route
            path="/login"
            element={!token ? <Login onLogin={handleLogin} /> : <Navigate to="/dashboard" />}
          />
          <Route
            path="/register"
            element={
              !token ? (
                <Register onLogin={handleLogin} />
              ) : !localStorage.getItem('onboarding_completed') ? (
                <Navigate to="/onboarding" />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/onboarding"
            element={token ? <Onboarding /> : <Navigate to="/login" />}
          />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          
          {/* Advisor Public Routes */}
          <Route path="/advisors" element={<AdvisorLandingPage />} />
          <Route path="/advisor/login" element={<AdvisorLogin onLogin={handleLogin} />} />
          <Route path="/advisor/signup" element={<AdvisorSignup />} />
          <Route path="/advisor/forgot-password" element={<ForgotPassword />} />
          
          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              token ? (
                !localStorage.getItem('onboarding_completed') ? 
                  <Navigate to="/onboarding" /> : 
                  user?.role === 'advisor' ?
                    <Navigate to="/advisor/dashboard" /> :
                    <Dashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route
            path="/transactions"
            element={token ? <Transactions user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/budgets"
            element={token ? <Budgets user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/goals"
            element={token ? <Goals user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/investments"
            element={token ? <Investments user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/discover-investments"
            element={token ? <DiscoverInvestments user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/stock-screener"
            element={token ? <StockScreener user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/market-news"
            element={token ? <MarketNews user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/quality-picks"
            element={token ? <QualityPicks user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/learn"
            element={token ? <LearnHome user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/learn/why-invest"
            element={token ? <WhyInvest user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/learn/risk-return"
            element={token ? <RiskReturn user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/learn/compounding"
            element={token ? <Compounding user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/learn/financial-statements"
            element={token ? <FinancialStatements user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/learn/key-metrics"
            element={token ? <KeyMetrics user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/learn/intrinsic-value"
            element={token ? <IntrinsicValueLesson user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/learn/buffett-philosophy"
            element={token ? <BuffettPhilosophy user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/learn/margin-of-safety"
            element={token ? <MarginOfSafety user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/learn/calculator/intrinsic-value"
            element={token ? <IntrinsicValueCalculator user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/learn/calculator/valuation"
            element={token ? <StockValuationCalculator user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/bills"
            element={token ? <Bills user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/loans"
            element={token ? <Loans user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/financial-planner"
            element={token ? <FinancialPlanner user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          {/* Advisor Pro Routes */}
          <Route
            path="/advisor/dashboard"
            element={token ? <AdvisorDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/advisor/login" />}
          />
          <Route
            path="/advisor/pricing"
            element={token ? <AdvisorPricing user={user} onLogout={handleLogout} /> : <Navigate to="/advisor/login" />}
          />
          <Route
            path="/advisor/clients"
            element={token ? <ClientDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/advisor/login" />}
          />
          <Route
            path="/advisor/create-plan"
            element={token ? <CreateProfessionalPlan user={user} onLogout={handleLogout} /> : <Navigate to="/advisor/login" />}
          />
          <Route
            path="/advisor/plan/:planId"
            element={token ? <ViewProfessionalPlan user={user} onLogout={handleLogout} /> : <Navigate to="/advisor/login" />}
          />

          <Route
            path="/advisor/plans"
            element={token ? <MyPlans user={user} onLogout={handleLogout} /> : <Navigate to="/advisor/login" />}
          />
          <Route
            path="/advisor/settings"
            element={token ? <AdvisorSettings user={user} onLogout={handleLogout} /> : <Navigate to="/advisor/login" />}
          />
          <Route
            path="/advisor/ai-chat"
            element={token ? <AdvisorAIChat user={user} onLogout={handleLogout} /> : <Navigate to="/advisor/login" />}
          />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route
            path="/admin/manage-advisors"
            element={token ? <ManageAdvisors user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />

          <Route
            path="/account-settings"
            element={token ? <AccountSettings user={user} onLogout={handleLogout} setUser={setUser} /> : <Navigate to="/login" />}
          />
          <Route
            path="/reports"
            element={token ? <Reports user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/ai-advisor"
            element={token ? <AIAdvisor user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/portfolio-analyzer"
            element={token ? <PortfolioAnalyzer user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/spending-insights"
            element={token ? <SpendingInsights user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/retirement-calculator"
            element={token ? <RetirementCalculator user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/net-worth"
            element={token ? <NetWorthTracker user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/alerts"
            element={token ? <SmartAlerts user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/calculators"
            element={token ? <FinancialCalculators user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/loan-calculators"
            element={token ? <LoanCalculators user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </div>
    </ThemeProvider>
  );
}

export default App;